import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {addYears, startOfToday} from 'date-fns';
import {Location} from '@/schemas/sub-schemas';
import {Sport} from '@/types/common';
import {CompanyInUserInfos} from '@/types/company';

const bDayMin = addYears(startOfToday(), -16);

const defaultUserInfo: UserInfosFormValues = {
  _id: '',
  name: '',
  birthday: bDayMin.toISOString(),
  description: '',
  hometown: '',
  gender: '',
  location: null,
  color: 'primary',
  role: 'USER',
  sports: [],
  email: '',
  picture: null,
  companies: [],
  avatarDeleted: true,
  updatedAt: null,
  userBlacklist: [],
  odooCompany: null
};

export interface UserInfosFormValues {
  _id: string;
  name: string;
  birthday: string;
  description: string;
  hometown: string;
  gender: string;
  location: Location | null;
  color: string;
  role?: string;
  sports?: Array<{sport: Sport; level: number}>;
  email?: string;
  picture?: string | null;
  companies?: CompanyInUserInfos[];
  avatarDeleted?: boolean;
  updatedAt: string | null;
  userBlacklist: string[];
  odooCompany?: string | null;
}

interface UserInfosState {
  userInfos: UserInfosFormValues;
  userInfosForm: UserInfosFormValues;
}

export const initialState: UserInfosState = {
  userInfos: {
    ...defaultUserInfo
  },
  userInfosForm: {
    ...defaultUserInfo
  }
};

const userInfosFormSlice = createSlice({
  name: 'userInfosForm',
  initialState,
  reducers: {
    setUserInfosFormData(
      state,
      action: PayloadAction<Partial<UserInfosFormValues>>
    ) {
      const payload = {...action.payload};

      if (payload.sports) {
        payload.sports = payload.sports.reduce(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (accumulator: any[], sport: any) => {
            if (sport.sport) {
              accumulator.push({
                sport: sport.sport._id ? sport.sport._id : sport.sport,
                level: sport.level
              });
            }
            return accumulator;
          },
          []
        );
      }

      state.userInfosForm = {
        ...state.userInfosForm,
        ...payload
      };
    },

    resetUserInfosFormData(state) {
      const {picture, ...restOfUserInfos} = state.userInfos;

      state.userInfosForm = {
        ...restOfUserInfos,
        avatarDeleted: picture ? false : true,
        picture
      };
    },

    purgeUserInfos(state) {
      state.userInfos = initialState.userInfos;
    },

    setUserInfos(state, action: PayloadAction<Partial<UserInfosFormValues>>) {
      state.userInfos = {
        ...state.userInfos,
        ...action.payload
      };
    },

    updateUserSports(
      state,
      action: PayloadAction<Array<{sport: Sport; level: number}>>
    ) {
      state.userInfos.sports = action.payload;
      state.userInfosForm.sports = action.payload;
    }
  }
});

export const {
  setUserInfosFormData,
  resetUserInfosFormData,
  setUserInfos,
  updateUserSports,
  purgeUserInfos
} = userInfosFormSlice.actions;
export default userInfosFormSlice.reducer;
